// Typed fetcher to the API worker. Base URL auto-selects by hostname
// (dev-excalidraw → api dev, prod → api prod, localhost → local).

const detectApiBase = (): string => {
  if (typeof window === 'undefined') return '';
  const host = window.location.hostname;
  // Prod domain → prod worker (writes to prod_* tables)
  if (host === 'excalidraw.extapia.com')
    return 'https://excalidraw-extapia-api-prod.extapia.workers.dev';
  // Dev domain + preview URLs → dev worker (writes to dev_* tables)
  if (host === 'dev-excalidraw.extapia.com' || host.endsWith('.pages.dev'))
    return 'https://excalidraw-extapia-api.extapia.workers.dev';
  return 'http://localhost:8787';
};

const API_BASE = detectApiBase();

interface ShareInput {
  docId: string;
  permission: 'view' | 'comment' | 'edit';
  expiresInSec?: number;
}

interface ShareOutput {
  token: string;
  url: string;
}

interface ShareInfo {
  docId: string;
  permission: 'view' | 'comment' | 'edit';
}

const request = async <T,>(path: string, init?: RequestInit): Promise<T> => {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
    credentials: 'include',
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`API ${res.status}: ${text || res.statusText}`);
  }
  return (await res.json()) as T;
};

export const api = {
  health: () => request<{ ok: boolean; env: string; time: string }>('/health'),
  createShare: (body: ShareInput) =>
    request<ShareOutput>('/share', { method: 'POST', body: JSON.stringify(body) }),
  resolveShare: (token: string) => request<ShareInfo>(`/share/${encodeURIComponent(token)}`),
  requestMagicLink: (email: string) =>
    request<{ status: string }>('/auth/magic-link', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  me: () => request<{ user: { id: string; email: string; name?: string } | null }>('/auth/me'),
};

export const apiBase = API_BASE;
